/**
 * VisibilityControls - Form component for managing template visibility
 *
 * Features:
 * - Radio group for visibility type selection
 * - Conditional plan/tenant multi-select
 * - Live preview of affected accounts
 */
import { useState, useEffect } from 'react';
import { Globe, CreditCard, Building2, EyeOff, Check } from 'lucide-react';
import {
  Label,
  RadioGroup,
  RadioGroupItem,
  Checkbox,
  cn,
} from '@respectly/ui';
import type { TemplateVisibility } from '@respectly/shared-types';
import { usePlansStore } from '../../stores/usePlansStore';

// ============================================
// Types
// ============================================

interface VisibilityControlsProps {
  value: TemplateVisibility;
  visibleToPlans?: string[];
  visibleToTenants?: string[];
  onChange: (visibility: TemplateVisibility, plans?: string[], tenants?: string[]) => void;
  className?: string;
}

interface VisibilityOption {
  value: TemplateVisibility;
  label: string;
  description: string;
  icon: typeof Globe;
}

// ============================================
// Configuration
// ============================================

const visibilityOptions: VisibilityOption[] = [
  {
    value: 'all',
    label: 'Public',
    description: 'Visible to all tenants on any plan',
    icon: Globe,
  },
  {
    value: 'plans',
    label: 'Plan-Specific',
    description: 'Only visible to tenants on selected plans',
    icon: CreditCard,
  },
  {
    value: 'tenants',
    label: 'Account-Specific',
    description: 'Only visible to specific accounts',
    icon: Building2,
  },
  {
    value: 'none',
    label: 'Hidden',
    description: 'Not visible to any tenants (draft/internal)',
    icon: EyeOff,
  },
];

// ============================================
// Component
// ============================================

export function VisibilityControls({
  value,
  visibleToPlans = [],
  visibleToTenants = [],
  onChange,
  className,
}: VisibilityControlsProps) {
  const [selectedPlans, setSelectedPlans] = useState<string[]>(visibleToPlans);
  // Note: Tenant selection is a future feature - keeping state for when it's implemented
  const [selectedTenants] = useState<string[]>(visibleToTenants);

  // Load plans for selection
  const { plans, loadPlans, isLoadingList } = usePlansStore();

  useEffect(() => {
    if (value === 'plans' && plans.length === 0) {
      loadPlans();
    }
  }, [value, plans.length, loadPlans]);

  // Handle visibility change
  const handleVisibilityChange = (newVisibility: TemplateVisibility) => {
    onChange(newVisibility, selectedPlans, selectedTenants);
  };

  // Handle plan selection
  const handlePlanToggle = (planId: string) => {
    const newPlans = selectedPlans.includes(planId)
      ? selectedPlans.filter((id) => id !== planId)
      : [...selectedPlans, planId];
    setSelectedPlans(newPlans);
    onChange(value, newPlans, selectedTenants);
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Visibility Type Selection */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Visibility</Label>
        <RadioGroup
          value={value}
          onValueChange={(val) => handleVisibilityChange(val as TemplateVisibility)}
          className="grid gap-3"
        >
          {visibilityOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = value === option.value;

            return (
              <label
                key={option.value}
                className={cn(
                  'flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all',
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                )}
              >
                <RadioGroupItem value={option.value} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon className={cn(
                      'h-4 w-4',
                      isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500'
                    )} />
                    <span className={cn(
                      'font-medium',
                      isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-900 dark:text-white'
                    )}>
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                    {option.description}
                  </p>
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </div>

      {/* Plan Selection (when visibility is 'plans') */}
      {value === 'plans' && (
        <div className="space-y-3">
          <Label className="text-sm font-medium">Select Plans</Label>
          <div className="border rounded-lg divide-y dark:border-slate-700 dark:divide-slate-700 max-h-64 overflow-y-auto">
            {isLoadingList ? (
              <div className="p-4 text-center text-sm text-slate-500">
                Loading plans...
              </div>
            ) : plans.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                No plans available
              </div>
            ) : (
              plans.map((plan) => (
                <label
                  key={plan.id}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedPlans.includes(plan.id)}
                    onCheckedChange={() => handlePlanToggle(plan.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-slate-900 dark:text-white">
                      {plan.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {plan.subscriberCount} subscribers
                    </div>
                  </div>
                  {selectedPlans.includes(plan.id) && (
                    <Check className="h-4 w-4 text-blue-600" />
                  )}
                </label>
              ))
            )}
          </div>
          {selectedPlans.length > 0 && (
            <p className="text-sm text-slate-500">
              {selectedPlans.length} plan{selectedPlans.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>
      )}

      {/* Tenant Selection (when visibility is 'tenants') */}
      {value === 'tenants' && (
        <div className="space-y-3">
          <Label className="text-sm font-medium">Select Accounts</Label>
          <div className="border rounded-lg p-4 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
              Account selection will be implemented with search functionality.
              <br />
              Use the Accounts page to assign templates to specific accounts.
            </p>
          </div>
          {selectedTenants.length > 0 && (
            <p className="text-sm text-slate-500">
              {selectedTenants.length} account{selectedTenants.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>
      )}
    </div>
  );
}
